/**
 * Hook to load and manage dataset manifest
 * Fetches datasets.json on mount and populates appStore
 */

import { useEffect } from 'react';
import { useAppStore } from '@store/appStore';
import { DatasetsManifest } from '../types/dataset';

import { apiUrl } from '../config';

const API_DATASETS_PATH = apiUrl('/api/datasets');

export function useDatasets() {
  const setAvailableDatasets = useAppStore((state) => state.setAvailableDatasets);
  const setCurrentDataset = useAppStore((state) => state.setCurrentDataset);
  const loadDataset = useAppStore((state) => state.loadDataset);
  const availableDatasets = useAppStore((state) => state.availableDatasets);
  const currentDatasetId = useAppStore((state) => state.currentDatasetId);
  const isLoadingDatasets = useAppStore((state) => state.isLoadingDatasets);

  useEffect(() => {
    async function loadDatasetsManifest() {
      try {
        // Try API first (Cosmos DB)
        let response = await fetch(API_DATASETS_PATH);
        let source = 'API';
        
        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }
        
        if (!response.ok) {
          throw new Error(`Failed to fetch datasets: ${response.statusText}`);
        }
        
        const manifest: DatasetsManifest = await response.json();
        setAvailableDatasets(manifest.datasets);
        console.log(`✅ [useDatasets] Loaded ${manifest.datasets.length} total datasets from ${source}`);
        
        // Debug: Log all datasets grouped by video
        console.log(`📊 [useDatasets] Dataset breakdown:`);
        const byVideo: Record<string, string[]> = {};
        manifest.datasets.forEach(ds => {
          const video = ds.video || 'unknown';
          if (!byVideo[video]) byVideo[video] = [];
          byVideo[video].push(ds.id);
        });
        
        Object.entries(byVideo).forEach(([video, ids]) => {
          console.log(`  📹 ${video}: ${ids.length} dataset(s)`);
          ids.forEach(id => console.log(`    • ${id}`));
        });
        
      } catch (error) {
        console.error('[useDatasets] Failed to load manifest:', error);
      }
    }

    if (availableDatasets.length === 0 && !isLoadingDatasets) {
      loadDatasetsManifest();
    }
  }, [availableDatasets.length, isLoadingDatasets, currentDatasetId, setAvailableDatasets, loadDataset]);

  return {
    availableDatasets,
    currentDatasetId,
    isLoadingDatasets,
    setCurrentDataset,
    loadDataset,
  };
}
